// Types for the Remote Work app

export interface JobType {
  id: string
  position: string | undefined
  company: string
  description: string
  url: string
  date: string
  tags?: string[]
  dateFormated?: string
  salary?: string
  isFavorite?: boolean
  image?: string
  logo?: string
  company_logo?: string
}

export type TemplateId = 'modern' | 'classic' | 'minimal'

export type ResumeColorMode = 'light' | 'dark'
export type ResumeFontFamilyId = 'sans' | 'serif' | 'modern' | 'mono'

export interface ResumeFontScale {
  title: number
  subtitle: number
  body: number
}

export interface ResumePersonalization {
  colorMode: ResumeColorMode
  fontFamily: ResumeFontFamilyId
  fontScale: ResumeFontScale
}

export interface ResumePersonalInfo {
  fullName: string
  title: string
  email: string
  phoneCountryCode: string
  phone: string
  location: string
  website: string
  summary: string
  // Data URI ("data:image/jpeg;base64,…") or '' when no photo is set.
  photo: string
}

export interface ResumeExperienceItem {
  id: string
  company: string
  position: string
  location: string
  startDate: string
  endDate: string
  current: boolean
  description: string
}

export interface ResumeEducationItem {
  id: string
  institution: string
  degree: string
  field: string
  startDate: string
  endDate: string
  description: string
}

export interface ResumeLanguageItem {
  name: string
  proficiency: string
}

export interface ResumeLinkItem {
  label: string
  url: string
}

export interface ResumeDocument {
  id: string
  name: string
  template: TemplateId
  createdAt: string
  updatedAt: string
  personalInfo: ResumePersonalInfo
  experience: ResumeExperienceItem[]
  education: ResumeEducationItem[]
  skills: string[]
  languages: ResumeLanguageItem[]
  links: ResumeLinkItem[]
  personalization: ResumePersonalization
}

export type ApplicationStatus = 'applied' | 'interviewing' | 'offer' | 'rejected'

export interface Application {
  jobId: string
  status: ApplicationStatus
  appliedAt: string
  updatedAt: string
  notes: string
  // ISO date recorded for each stage the application has reached, so the user
  // can see and edit when they applied, interviewed, etc.
  stageDates: Partial<Record<ApplicationStatus, string>>
  // Snapshot of the job at apply time so the tracker survives feed rotation
  // (jobs age out of the live list but the application is still tracked).
  position: string
  company: string
  url: string
}

// A saved search the user wants to be notified about when new matching jobs
// appear (delivered via Firebase Cloud Messaging from the scheduled function).
export interface JobAlert {
  id: string
  query: string // free-text keywords matched against position/tags/description
  tags: string[] // optional tag filters (job must have at least one)
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface ContextType {
  data: JobType[]
  keys: string[]
  applied: string[]
  applications: Record<string, Application>
  alerts: JobAlert[]
  refreshing: boolean
  error: boolean
  onboardingCompleted: boolean
  hydrated: boolean
  themeMode: 'light' | 'dark'
  language: string
  hideAds: boolean
  removeAdsEntitled: boolean
  disclaimerAcknowledged: boolean
  removeAdsPurchasing: boolean
  removeAdsPriceString: string | null
  removeAdsLastError: string | null
  resumes: ResumeDocument[]
  refresh: () => void
  handleFavorites?: (data: JobType) => void
  handleClearFavorites?: () => void
  handleApplied?: (job: JobType) => void
  setApplicationStatus: (jobId: string, status: ApplicationStatus) => void
  setApplicationNotes: (jobId: string, notes: string) => void
  setApplicationStageDate: (jobId: string, status: ApplicationStatus, isoDate: string) => void
  removeApplicationStage: (jobId: string, status: ApplicationStatus) => void
  removeApplication: (jobId: string) => void
  addManualApplication: (company: string, position: string) => void
  addAlert: (query: string, tags?: string[]) => JobAlert
  updateAlert: (alert: JobAlert) => void
  deleteAlert: (id: string) => void
  toggleAlert: (id: string) => void
  completeOnboarding: () => void
  acknowledgeDisclaimer: () => void
  setThemeMode: (mode: 'light' | 'dark') => void
  setLanguage: (lang: string) => void
  setHideAds: (hide: boolean) => void
  purchaseRemoveAds: () => Promise<{ outcome: 'purchased' | 'cancelled' | 'failed'; reason?: string }>
  restoreRemoveAds: () => Promise<{ outcome: 'restored' | 'empty' | 'failed'; reason?: string }>
  resetRemoveAdsForTesting: () => Promise<boolean>
  addResume: (name: string, template?: TemplateId) => ResumeDocument
  updateResume: (resume: ResumeDocument) => void
  deleteResume: (id: string) => void
  duplicateResume: (id: string) => ResumeDocument | null
  // Permanently delete the signed-in user's account + cloud data and clear the
  // local personal slices. Returns ok:false with the auth error code (e.g.
  // 'requires-recent-login') so the UI can guide a retry.
  deleteAccount: () => Promise<{ ok: boolean; reason?: string }>
}

export interface handleFavorites {
  add: () => void
  clear: (keys: string[]) => void
}

// Add dummy default export for Expo Router
export default {};
